import {
  Promotion,
  PromotionFormData,
  PromotionStats,
  PromotionApiResponse,
  PromotionStatsApiResponse,
  SinglePromotionApiResponse,
  PromotionFilters
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Helper function to get auth token
const getAuthToken = (): string => {
  return localStorage.getItem('accessToken') || '';
};

// Helper function to get default headers
const getDefaultHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

class PromotionsService {
  private baseUrl = `${API_BASE_URL}/promotions`;

  // Obtener todas las promociones con filtros opcionales
  async getPromotions(filters: PromotionFilters = {}): Promise<Promotion[]> {
    const params = new URLSearchParams();

    // Agregar filtros como parámetros de consulta
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.type && filters.type !== 'all') {
      params.append('type', filters.type);
    }
    if (filters.isActive !== undefined && filters.isActive !== 'all') {
      params.append('isActive', filters.isActive.toString());
    }

    const url = params.toString() ? `${this.baseUrl}?${params.toString()}` : this.baseUrl;

    const response = await fetch(url, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: PromotionApiResponse = await response.json();

    if (!result.success) {
      throw new Error('Error al obtener promociones');
    }

    return result.data;
  }

  // Obtener estadísticas de promociones
  async getPromotionStats(): Promise<PromotionStats> {
    const response = await fetch(`${this.baseUrl}/dashboard/stats`, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: PromotionStatsApiResponse = await response.json();

    if (!result.success) {
      throw new Error('Error al obtener estadísticas');
    }

    return result.data;
  }

  // Obtener una promoción por ID
  async getPromotionById(id: number): Promise<Promotion | null> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: SinglePromotionApiResponse = await response.json();

    if (!result.success) {
      throw new Error('Error al obtener promoción');
    }

    return result.data;
  }

  // Crear una nueva promoción
  async createPromotion(data: PromotionFormData): Promise<Promotion> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: getDefaultHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
    }

    const result: SinglePromotionApiResponse = await response.json();

    if (!result.success) {
      throw new Error(result.data ? 'Error al crear promoción' : 'Error al crear promoción');
    }

    return result.data;
  }

  // Actualizar una promoción existente
  async updatePromotion(id: number, data: Partial<PromotionFormData>): Promise<Promotion> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: getDefaultHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
    }

    const result: SinglePromotionApiResponse = await response.json();

    if (!result.success) {
      throw new Error('Error al actualizar promoción');
    }

    return result.data;
  }

  // Eliminar una promoción
  async deletePromotion(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error('Error al eliminar promoción');
    }
  }

  // Activar/desactivar una promoción
  async togglePromotionStatus(id: number, isActive: boolean): Promise<Promotion> {
    return this.updatePromotion(id, { isActive });
  }

  // Aplicar promoción a un carrito (método auxiliar para cálculos del frontend)
  async applyPromotionToCart(promotionId: number, cart: any): Promise<any> {
    const response = await fetch(`${this.baseUrl}/${promotionId}/apply`, {
      method: 'POST',
      headers: getDefaultHeaders(),
      body: JSON.stringify({ cart }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error('Error al aplicar promoción');
    }

    return result.data;
  }
}

export const promotionsService = new PromotionsService();